import {
  WAYPOINTS_SOURCE,
  LINES_SOURCE,
  TRACKS_SOURCE,
  SHAPES_SOURCE,
  ROUTES_SOURCE,
} from '@/lib/enums'
import {
  TIME_TO_WAYPOINTS_RENDER,
  TIME_TO_WAYPOINTS_RENDER_START,
  TIME_TO_WAYPOINTS_RENDER_STOP,
  TIME_TO_LINES_RENDER,
  TIME_TO_LINES_RENDER_START,
  TIME_TO_LINES_RENDER_STOP,
  TIME_TO_TRACKS_RENDER,
  TIME_TO_TRACKS_RENDER_START,
  TIME_TO_TRACKS_RENDER_STOP,
  TIME_TO_SHAPES_RENDER,
  TIME_TO_SHAPES_RENDER_START,
  TIME_TO_SHAPES_RENDER_STOP,
  TIME_TO_ROUTES_RENDER,
  TIME_TO_ROUTES_RENDER_START,
  TIME_TO_ROUTES_RENDER_STOP,
} from '@/lib/performance-marks'
import markupService from '@/services/markup-service'
import type { MapManager } from '@onxmaps/map-x'
import type { EventMap, MapXSourceData } from '@onxmaps/map-x/events'

/**
 * Both of these flags are used to prevent multiple performance marks from being created
 * since multiple sourcedata events are fired for each waypoint source
 */
interface PerformanceRunStatus {
  running: boolean
  completed: boolean
}

interface PerformanceMark {
  name: string
  start: string
  stop: string
}

export const statusMap = new Map<string, PerformanceRunStatus>([
  [
    WAYPOINTS_SOURCE,
    {
      running: false,
      completed: false,
    },
  ],
  [
    LINES_SOURCE,
    {
      running: false,
      completed: false,
    },
  ],
  [
    TRACKS_SOURCE,
    {
      running: false,
      completed: false,
    },
  ],
  [
    SHAPES_SOURCE,
    {
      running: false,
      completed: false,
    },
  ],
  [
    ROUTES_SOURCE,
    {
      running: false,
      completed: false,
    },
  ],
])

/**
 * Map of callbacks for getters based on markup type.
 * Checks if markup type has any features loaded based on getter length
 */
const hasMarkupsCbMap = new Map<string, { cb: () => boolean }>([
  [WAYPOINTS_SOURCE, { cb: () => markupService.getWaypoints().length > 0 }],
  [LINES_SOURCE, { cb: () => markupService.getLines().length > 0 }],
  [TRACKS_SOURCE, { cb: () => markupService.getTracks().length > 0 }],
  [SHAPES_SOURCE, { cb: () => markupService.getShapes().length > 0 }],
  [ROUTES_SOURCE, { cb: () => markupService.getRoutes().length > 0 }],
])

// Map of performance marks for each markup type
const markMap = new Map<string, PerformanceMark>([
  [
    WAYPOINTS_SOURCE,
    {
      name: TIME_TO_WAYPOINTS_RENDER,
      start: TIME_TO_WAYPOINTS_RENDER_START,
      stop: TIME_TO_WAYPOINTS_RENDER_STOP,
    },
  ],
  [
    LINES_SOURCE,
    {
      name: TIME_TO_LINES_RENDER,
      start: TIME_TO_LINES_RENDER_START,
      stop: TIME_TO_LINES_RENDER_STOP,
    },
  ],
  [
    TRACKS_SOURCE,
    {
      name: TIME_TO_TRACKS_RENDER,
      start: TIME_TO_TRACKS_RENDER_START,
      stop: TIME_TO_TRACKS_RENDER_STOP,
    },
  ],
  [
    SHAPES_SOURCE,
    {
      name: TIME_TO_SHAPES_RENDER,
      start: TIME_TO_SHAPES_RENDER_START,
      stop: TIME_TO_SHAPES_RENDER_STOP,
    },
  ],
  [
    ROUTES_SOURCE,
    {
      name: TIME_TO_ROUTES_RENDER,
      start: TIME_TO_ROUTES_RENDER_START,
      stop: TIME_TO_ROUTES_RENDER_STOP,
    },
  ],
])

/**
 * Initialize the performance metrics for the markup render timing
 * and listens for the source-data event on the map
 * @param mapManager - MapManager instance
 */
export const initMarkupRenderTimingPerformanceMetrics = (
  mapManager: MapManager
) => {
  mapManager.events.on(
    'map-x-source-data',
    (event: EventMap['map-x-source-data']) => {
      if (event.detail) {
        handleSourceDataEvent(event.detail)
      }
    }
  )
}

/**
 * Handles the sourcedata events and starts and stops
 * the performance measurement based on the event and sourceId
 * @param eventDetail - MapXSourceData
 */
export const handleSourceDataEvent = (eventDetail: MapXSourceData) => {
  const source = eventDetail?.sourceId
  if (
    source !== undefined &&
    isMarkupSource(source) &&
    // Check if markups have been loaded from api
    hasMarkupsCbMap.get(source)?.cb()
  ) {
    const mark = markMap.get(source)
    // Start measurement
    if (hasRenderStarted(eventDetail, source) && mark) {
      performance.mark(mark.start)
      statusMap.set(source, { running: true, completed: false })
    }
    // Finish measurement
    if (hasRenderCompleted(eventDetail, source) && mark) {
      statusMap.set(source, { running: false, completed: true })
      performance.mark(mark.stop)
      measureRenderTime(source)
    }
  }
}

/**
 * Checks if the source is a markup source
 * @param source - sourceId of sourcedata event
 * @returns - Wether the source is a markup source
 */
const isMarkupSource = (source: string) =>
  source === WAYPOINTS_SOURCE ||
  source === LINES_SOURCE ||
  source === TRACKS_SOURCE ||
  source === SHAPES_SOURCE ||
  source === ROUTES_SOURCE

/**
 * Check if the render for the markup type has started
 * based on the sourceId of the event.
 * Also makes sure the measurement hasn't already been started or
 * completed for the current map load
 * @param eventDetail - MapXSourceData
 * @param source - the markup source type
 * @returns - whether the render has started
 */
const hasRenderStarted = (eventDetail: MapXSourceData, source: string) =>
  !statusMap.get(source)?.completed &&
  !statusMap.get(source)?.running &&
  eventDetail.sourceId === source &&
  !eventDetail.isSourceLoaded

/**
 * Checks if the render for markup source has completed
 * based on the sourceId of the event and isSourceLoaded flag
 * @param eventDetail - MapXSourceData
 * @param source - the markup source type
 * @returns - whether the render has completed
 */
const hasRenderCompleted = (eventDetail: MapXSourceData, source: string) => {
  return (
    !statusMap.get(source)?.completed &&
    statusMap.get(source)?.running &&
    eventDetail.sourceId === source &&
    // isSourceLoaded seems to be switched to true before the features are rendered on map
    // so we need to also check that the features are in the source.data array to be sure
    eventDetail.isSourceLoaded &&
    // @ts-expect-error source.data is not available on all source types
    eventDetail.source?.data?.features?.length
  )
}

/**
 * Measure the time it took to render the markup source
 * @param source - the markup source type
 * @returns - the performance measure for the waypoint render time
 */
const measureRenderTime = (source: string) => {
  const mark = markMap.get(source)
  return mark
    ? performance.measure(mark.name, mark.start, mark.stop)
    : undefined
}
